/**
 * WeekView Component
 *
 * Displays bookings in a week view format with employee rows
 * and vertical daily booking cards.
 *
 * Uses the Accordion component for expandable employee sections.
 */

import { Accordion } from "@/components/ui/display/Accordion";
import { UserBadge } from "@/components/ui/display/UserBadge";
import { AggregateRow } from "@/components/schedule/Schedule/AggregateRow";
import { EditModeButton } from "@/components/shared/EditModeButton";
import { BookingCard } from "@/components/booking/display/BookingCard";
import { Legends } from "@/components/booking/display/Legends";
import { formatEmployeeName } from "@/hooks/data/dataQuality";
import classNames from "classnames";
import React, { useRef, useState, useEffect, useMemo } from "react";
import styles from "../Schedule.module.css";
import type { WeekViewProps } from "../types";

/** Breakpoint for compact layout (pills above card) */
const COMPACT_BREAKPOINT = 480;

export const WeekView: React.FC<WeekViewProps> = React.memo(
  ({
    employees,
    visibleDays,
    hoursInDay,
    collapsedEmployees,
    toggleEmployeeCollapse,
    selectMode,
    selectedCardId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used by React.memo to trigger re-render when selection changes (Bug #9022)
    selectedIds: _selectedIds,
    isSelected,
    isInRangePreview,
    setRangeHoverTarget,
    handleCardClick,
    onCardClick,
    getEmployeeStats,
    getCardForDay,
    hideAggregate = false,
    // Per-employee edit mode props
    showIndividualEditButtons = false,
    editingEmployeeId,
    onEnterEmployeeEditMode,
    onExitEmployeeEditMode,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isCompact, setIsCompact] = useState(false);

    // Monitor container width for compact layout
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          setIsCompact(width < COMPACT_BREAKPOINT);
        }
      });

      observer.observe(container);

      // Initial check
      setIsCompact(container.offsetWidth < COMPACT_BREAKPOINT);

      return () => observer.disconnect();
    }, []);

    // Convert collapsed set to expanded array for Accordion (inverted logic)
    // Accordion expects array of expanded item values
    const expandedValues = useMemo(() => {
      return employees
        .filter((emp) => !collapsedEmployees.has(emp.id))
        .map((emp) => String(emp.id));
    }, [employees, collapsedEmployees]);

    // Handle accordion value change - toggle the employee
    const handleAccordionChange = (value: string | string[]) => {
      // Accordion returns array in multiple mode
      const newExpanded = Array.isArray(value) ? value : [value];

      // Find which employee changed by comparing with current state
      for (const employee of employees) {
        const empId = String(employee.id);
        const wasExpanded = expandedValues.includes(empId);
        const isNowExpanded = newExpanded.includes(empId);

        if (wasExpanded !== isNowExpanded) {
          toggleEmployeeCollapse(employee.id);
        }
      }
    };

    return (
      <div
        ref={containerRef}
        className={styles.weekView}
        data-testid="schedule-week-view"
        data-compact={isCompact || undefined}
      >
        <Accordion
          type="multiple"
          value={expandedValues}
          onValueChange={handleAccordionChange}
        >
          {employees.map((employee) => {
            const stats = getEmployeeStats(employee);

            return (
              <Accordion.Item
                key={employee.id}
                value={String(employee.id)}
                className={styles.entityRow}
              >
                {/* Employee Header - Name Row */}
                <Accordion.Trigger
                  chevronVariant="minimal"
                  chevronClassName={classNames(
                    styles.collapseButton,
                    styles.entityCollapseButton,
                  )}
                  className={classNames(
                    styles.entityHeader,
                    isCompact && styles.entityHeaderCompact,
                  )}
                  data-testid={`collapse-toggle-${employee.id}`}
                >
                  {/* Employee Name Badge */}
                  <div className={styles.entityChip}>
                    <UserBadge
                      name={formatEmployeeName(employee)}
                      size="sm"
                      mode="auto"
                      data-testid={`employee-badge-${employee.id}`}
                    />
                  </div>

                  {/* Spacer - hidden in compact mode via CSS */}
                  <div className={styles.entitySpacer} />

                  {/* Aggregate Row - Progress stats (hidden for soft bookings/customer mode) */}
                  {!hideAggregate && (
                    <div className={styles.entityAggregate}>
                      <AggregateRow
                        bookedHours={stats.totalHours}
                        possibleHours={stats.totalCapacity}
                        data-testid={`aggregate-${employee.id}`}
                      />
                    </div>
                  )}
                </Accordion.Trigger>

                {/* Daily Bookings - Vertical List */}
                <Accordion.Content className={styles.entityContent}>
                  <div className={styles.bookingList}>
                    {visibleDays.map((day) => {
                      const cardData = getCardForDay(employee, day);
                      // Check if this employee's cells are interactive
                      // When another employee is in edit mode, this employee's cells should NOT be clickable
                      const isInteractive =
                        editingEmployeeId === null ||
                        editingEmployeeId === employee.id;

                      return (
                        <BookingCard
                          key={day.toISOString()}
                          card={cardData}
                          view="week"
                          dayHours={hoursInDay}
                          showDate
                          selectable={selectMode && isInteractive}
                          compact={isCompact}
                          selected={
                            selectMode
                              ? isSelected(cardData.id)
                              : cardData.id === selectedCardId
                          }
                          inRangePreview={
                            selectMode && isInRangePreview(cardData.id)
                          }
                          onClick={
                            isInteractive
                              ? (e, card) => {
                                  handleCardClick(
                                    card.id,
                                    {
                                      shift: e.shiftKey,
                                      ctrl: e.ctrlKey,
                                      meta: e.metaKey,
                                    },
                                    onCardClick,
                                  );
                                }
                              : undefined
                          }
                          onMouseEnter={
                            selectMode && isInteractive
                              ? (e, card) => {
                                  // Only set range target when shift is held (Bug #9023)
                                  if (e.shiftKey) {
                                    setRangeHoverTarget(card.id);
                                  } else {
                                    setRangeHoverTarget(null);
                                  }
                                }
                              : undefined
                          }
                          data-testid={`booking-card-${
                            employee.id
                          }-${day.toISOString()}`}
                        />
                      );
                    })}
                  </div>

                  {/* Individual Edit Button (when multiple employees selected) */}
                  {showIndividualEditButtons && (
                    <div className={styles.employeeEditButtonContainer}>
                      <EditModeButton
                        enabled={editingEmployeeId === employee.id}
                        disabled={
                          editingEmployeeId !== null &&
                          editingEmployeeId !== employee.id
                        }
                        onClick={() =>
                          editingEmployeeId === employee.id
                            ? onExitEmployeeEditMode?.()
                            : onEnterEmployeeEditMode?.(employee.id)
                        }
                        data-testid={
                          editingEmployeeId === employee.id
                            ? `employee-edit-mode-cancel-btn-${employee.id}`
                            : `employee-edit-mode-btn-${employee.id}`
                        }
                      />
                    </div>
                  )}
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion>

        <Legends direction="row" align="center" />
      </div>
    );
  },
);

WeekView.displayName = "WeekView";

export default WeekView;
